import { useEffect, useId, useMemo, useRef, useState, type Ref } from "react";
import {
  getAuthorableWatchSignals,
  type WatchSignalCatalog,
  type WatchSignalDefinition,
  type WatchSignalFamily,
} from "../../lib/watchSignalCatalog";
import {
  findAuthorableWatchRecipe,
  getAuthorableWatchRecipes,
  type WatchRecipeCatalog,
  type WatchRecipeDefinition,
} from "../../lib/watchRecipeCatalog";
import {
  getReviewedSignalPresenter,
  isCompletedDailyVolatilityPresenter,
  isMomentumPresenter,
  isMovingAveragePresenter,
} from "../../lib/watchSignalReleaseRegistry";
import WatchAdvancedSignalFamilies from "./WatchAdvancedSignalFamilies";
import WatchCatalogSignalDisclosure from "./WatchCatalogSignalDisclosure";
import GuidedWatchRecipes, { type SelectedWatchRecipeIdentity } from "./GuidedWatchRecipes";
import styles from "./WatchSignalPicker.module.css";

interface SelectedSignalIdentity {
  /** Stable public signal identifier. */
  signalId: string;
  /** Exact reviewed definition version. */
  definitionVersion: number;
}

export interface WatchSignalPickerProps {
  /** Runtime-decoded authenticated catalog. */
  catalog: WatchSignalCatalog | null;
  /** Explicit customer selection, or null before they choose. */
  selected: SelectedSignalIdentity | null;
  /** Receives an exact available definition chosen by the customer. */
  onSelect: (signal: WatchSignalDefinition) => void;
  /** Runtime-decoded immutable recipe catalog. Missing and empty stay invisible. */
  recipeCatalog?: WatchRecipeCatalog | null;
  /** Exact explicit recipe selection, or null before the customer chooses. */
  selectedRecipe?: SelectedWatchRecipeIdentity | null;
  /** Receives a reviewed immutable recipe, or null when returning to individual signals. */
  onSelectRecipe?: (recipe: WatchRecipeDefinition | null) => void;
  /** Marks the signal group invalid after an attempted step transition. */
  invalid?: boolean;
  /** ID of the visible validation message associated with the signal group. */
  errorMessageId?: string;
  /** Marks an expanded percentage reference choice invalid. */
  invalidReference?: boolean;
  /** Reports whether the customer opened the percentage reference chooser. */
  onPercentageExpandedChange?: (expanded: boolean) => void;
  /** Allows the wizard to focus the required percentage reference group. */
  percentageReferenceRef?: Ref<HTMLFieldSetElement>;
  /** Explicit Moving averages comparison, or null before the customer chooses. */
  selectedMovingAverageComparison?: "PRICE_TO_AVERAGE" | "AVERAGE_CROSSOVER" | null;
  /** Receives the plain-language Moving averages comparison choice. */
  onSelectMovingAverageComparison?: (
    comparison: "PRICE_TO_AVERAGE" | "AVERAGE_CROSSOVER",
  ) => void;
  /** Marks the expanded Moving averages comparison choices invalid. */
  invalidMovingAverageComparison?: boolean;
  /** Reports whether the customer opened the Moving averages chooser. */
  onMovingAverageExpandedChange?: (expanded: boolean) => void;
  /** Allows the wizard to focus the required Moving averages comparison group. */
  movingAverageComparisonRef?: Ref<HTMLFieldSetElement>;
  selectedMomentumIndicator?: "RSI" | "MACD" | "ROC" | null;
  onSelectMomentumIndicator?: (indicator: "RSI" | "MACD" | "ROC") => void;
  invalidMomentumIndicator?: boolean;
  onMomentumExpandedChange?: (expanded: boolean) => void;
  momentumIndicatorRef?: Ref<HTMLFieldSetElement>;
  selectedVolatilityMeasurement?:
    | "ATR14"
    | "BOLLINGER_PERCENT_B_20_2"
    | "BOLLINGER_BANDWIDTH_20_2"
    | "REALIZED_VOLATILITY_20D"
    | null;
  onVolatilityExpandedChange?: (expanded: boolean) => void;
  invalidVolatilityMeasurement?: boolean;
  volatilityMeasurementRef?: Ref<HTMLFieldSetElement>;
  /** Optional style extension applied to the root element. */
  className?: string;
}

type SignalView =
  | "POPULAR"
  | "RECIPES"
  | "IS_IT_MOVING"
  | "HOW_BIG_COULD_THE_MOVE_BE"
  | "ARE_OPTIONS_PRICING_IT_RICHLY"
  | "CAN_I_GET_IN_AND_OUT_EASILY";

interface SignalViewPresentation {
  readonly id: Exclude<SignalView, "POPULAR" | "RECIPES">;
  readonly label: string;
  readonly description: string;
  readonly families: ReadonlySet<WatchSignalFamily>;
}

// Compact filters group related measurements without duplicating their descriptions.
const SIGNAL_VIEWS: readonly SignalViewPresentation[] = [
  {
    id: "IS_IT_MOVING",
    label: "Price & trends",
    description: "Price, direction, and momentum.",
    families: new Set<WatchSignalFamily>(["PRICE", "TREND", "MOMENTUM", "VOLUME"]),
  },
  {
    id: "HOW_BIG_COULD_THE_MOVE_BE",
    label: "Volatility",
    description: "Realized and implied volatility, and expected range.",
    families: new Set<WatchSignalFamily>(["VOLATILITY"]),
  },
  {
    id: "ARE_OPTIONS_PRICING_IT_RICHLY",
    label: "Options pricing",
    description: "Put/call skew, term structure, and positioning.",
    families: new Set<WatchSignalFamily>(["OPTIONS_CHAIN"]),
  },
  {
    id: "CAN_I_GET_IN_AND_OUT_EASILY",
    label: "Liquidity",
    description: "Quote coverage and typical bid/ask spread.",
    families: new Set<WatchSignalFamily>(["LIQUIDITY"]),
  },
];

const ADVANCED_SIGNAL_FAMILIES = new Set<WatchSignalFamily>([
  "VOLATILITY",
  "OPTIONS_CHAIN",
  "LIQUIDITY",
]);

const POPULAR_SIGNAL_IDENTITIES = new Set([
  "underlying.price:1",
  "underlying.price_change.prior_regular_close:1",
  "underlying.price_change.regular_session_open:1",
  "underlying.completed_regular_close.relative_to_sma:1",
  "underlying.completed_regular_close.crosses_sma:1",
  "underlying.completed_regular_close.relative_to_ema:1",
  "underlying.completed_regular_close.crosses_ema:1",
  "underlying.sma.crosses_sma:1",
  "underlying.ema.crosses_ema:1",
  "underlying.rsi.relative_to_level:1",
  "underlying.rsi.crosses_level:1",
  "underlying.macd.line.crosses_signal:1",
  "underlying.macd.line.crosses_zero:1",
  "underlying.roc.relative_to_level:1",
  "underlying.roc.crosses_level:1",
  "underlying.atr.completed_daily.14.relative_to_level:1",
  "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level:1",
  "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level:1",
  "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level:1",
  "options_chain.atm_iv.standardized_30d.relative_to_level:1",
  "options_chain.expected_move.standardized_30d.relative_to_level:1",
]);

/** Select an authenticated, released MARKET signal through progressive disclosure. */
export default function WatchSignalPicker({
  catalog,
  selected,
  onSelect,
  recipeCatalog = null,
  selectedRecipe = null,
  onSelectRecipe,
  invalid = false,
  errorMessageId,
  invalidReference = false,
  onPercentageExpandedChange,
  percentageReferenceRef,
  selectedMovingAverageComparison = null,
  onSelectMovingAverageComparison,
  invalidMovingAverageComparison = false,
  onMovingAverageExpandedChange,
  movingAverageComparisonRef,
  selectedMomentumIndicator = null,
  onSelectMomentumIndicator,
  invalidMomentumIndicator = false,
  onMomentumExpandedChange,
  momentumIndicatorRef,
  onVolatilityExpandedChange,
  invalidVolatilityMeasurement = false,
  volatilityMeasurementRef,
  className,
}: WatchSignalPickerProps) {
  const choicesId = useId();
  const [view, setView] = useState<SignalView>("POPULAR");
  const [percentageExpanded, setPercentageExpanded] = useState<boolean | null>(null);
  const [movingAverageExpanded, setMovingAverageExpanded] = useState<boolean | null>(null);
  const [momentumExpanded, setMomentumExpanded] = useState<boolean | null>(null);
  const activeRecipe = selectedRecipe
    ? findAuthorableWatchRecipe(
      recipeCatalog,
      catalog,
      selectedRecipe.recipeId,
      selectedRecipe.definitionVersion,
    )
    : null;
  const wasActiveRecipeRef = useRef(Boolean(activeRecipe));
  useEffect(() => {
    // Returning from "Choose an individual signal instead" should land
    // somewhere with visible cards, not on the now-empty Recipes tab.
    if (wasActiveRecipeRef.current && !activeRecipe && view === "RECIPES") {
      setView("POPULAR");
    }
    wasActiveRecipeRef.current = Boolean(activeRecipe);
  }, [activeRecipe, view]);
  const signals = useMemo(
    () => getAuthorableWatchSignals(catalog, { job: "MARKET" }),
    [catalog],
  );
  const recipes = useMemo(
    () => getAuthorableWatchRecipes(recipeCatalog, catalog),
    [recipeCatalog, catalog],
  );
  const availableViews = useMemo(() => SIGNAL_VIEWS.filter((candidate) => (
    signals.some((signal) => candidate.families.has(signal.family))
  )), [signals]);
  const showRecipesView = Boolean(onSelectRecipe) && recipes.length > 0;
  const visibleSignals = useMemo(() => {
    if (view === "RECIPES") return [];
    if (view === "POPULAR") {
      return signals.filter((signal) => (
        POPULAR_SIGNAL_IDENTITIES.has(`${signal.signal_id}:${signal.definition_version}`)
      ));
    }
    const selectedView = SIGNAL_VIEWS.find((candidate) => candidate.id === view);
    return selectedView
      ? signals.filter((signal) => selectedView.families.has(signal.family))
      : [];
  }, [signals, view]);
  const percentageSignals = visibleSignals.filter(
    (signal) => getReviewedSignalPresenter(signal)?.kind === "PERCENTAGE_MOVE",
  );
  const standaloneSignals = visibleSignals.filter(
    (signal) => {
      const presenter = getReviewedSignalPresenter(signal);
      return presenter?.kind !== "PERCENTAGE_MOVE"
        && !isMovingAveragePresenter(presenter)
        && !isMomentumPresenter(presenter)
        && !isCompletedDailyVolatilityPresenter(presenter);
    },
  );
  const advancedSignals = standaloneSignals.filter((signal) => (
    ADVANCED_SIGNAL_FAMILIES.has(signal.family)
  ));
  const primaryStandaloneSignals = standaloneSignals.filter((signal) => (
    !ADVANCED_SIGNAL_FAMILIES.has(signal.family)
  ));
  const movingAverageSignals = visibleSignals.filter((signal) => (
    isMovingAveragePresenter(getReviewedSignalPresenter(signal))
  ));
  const momentumSignals = visibleSignals.filter((signal) => (
    isMomentumPresenter(getReviewedSignalPresenter(signal))
  ));
  const volatilitySignals = visibleSignals.filter((signal) => (
    isCompletedDailyVolatilityPresenter(getReviewedSignalPresenter(signal))
  ));
  const percentageSelected = percentageSignals.some((signal) => (
    selected?.signalId === signal.signal_id
    && selected.definitionVersion === signal.definition_version
  ));
  const showPercentageReferences = percentageExpanded ?? percentageSelected;
  const movingAverageSelected = selectedMovingAverageComparison !== null;
  const showMovingAverageComparisons = movingAverageExpanded ?? movingAverageSelected;
  const hasPriceAverage = movingAverageSignals.some((signal) => (
    getReviewedSignalPresenter(signal)?.kind === "MOVING_AVERAGE_RELATIONSHIP"
  ));
  const hasAverageCrossover = movingAverageSignals.some((signal) => (
    getReviewedSignalPresenter(signal)?.kind === "MOVING_AVERAGE_CROSSOVER"
  ));
  const showMomentumIndicators = momentumExpanded ?? selectedMomentumIndicator !== null;
  const hasMomentumIndicator = (indicator: "RSI" | "MACD" | "ROC") => (
    momentumSignals.some((signal) => (
      getReviewedSignalPresenter(signal)?.kind === `MOMENTUM_${indicator}`
    ))
  );

  return (
    <section className={`${styles.root} ${className ?? ""}`} aria-label="Market signals">
      {selectedRecipe && !activeRecipe ? (
        <p className={styles.empty} role="status">
          This guided recipe is no longer available. Choose another market condition.
        </p>
      ) : null}

      {activeRecipe ? (
        <>
          <GuidedWatchRecipes
            recipeCatalog={recipeCatalog}
            signalCatalog={catalog}
            selected={selectedRecipe}
            onSelect={onSelectRecipe ?? (() => undefined)}
          />
          <p className={styles.empty} role="status">
            Individual market signals are hidden while a guided recipe is
            selected. Use “Choose an individual signal instead” to browse them.
          </p>
        </>
      ) : signals.length > 0 ? (
        <>
          <div className={styles.filters} aria-label="Signal categories">
            <button
              type="button"
              className={view === "POPULAR" ? styles.filterSelected : styles.filter}
              aria-pressed={view === "POPULAR"}
              onClick={() => setView("POPULAR")}
            >
              Popular
            </button>
            {showRecipesView ? (
              <button
                type="button"
                aria-label="Show guided recipes"
                className={view === "RECIPES" ? styles.filterSelected : styles.filter}
                aria-pressed={view === "RECIPES"}
                onClick={() => setView("RECIPES")}
              >
                Recipes
              </button>
            ) : null}
            {availableViews.map((signalView) => (
              <button
                type="button"
                className={view === signalView.id ? styles.filterSelected : styles.filter}
                aria-pressed={view === signalView.id}
                onClick={() => setView(signalView.id)}
                key={signalView.id}
              >
                {signalView.label}
              </button>
            ))}
          </div>
          <p className={styles.viewLabel}>{view === "POPULAR"
            ? "Common starting points"
            : view === "RECIPES"
              ? "Choose a preset when several conditions should work together."
              : SIGNAL_VIEWS.find((candidate) => candidate.id === view)?.description}</p>
          {view === "RECIPES" ? (
            <GuidedWatchRecipes
              recipeCatalog={recipeCatalog}
              signalCatalog={catalog}
              selected={selectedRecipe}
              onSelect={onSelectRecipe ?? (() => undefined)}
            />
          ) : visibleSignals.length > 0 ? (
            <div
              className={styles.signalList}
              role="group"
              aria-label="Available market signals"
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? errorMessageId : undefined}
            >
              {primaryStandaloneSignals.map((signal) => {
                const isSelected = selected?.signalId === signal.signal_id
                  && selected.definitionVersion === signal.definition_version;
                return (
                  <button
                    type="button"
                    className={isSelected ? styles.signalSelected : styles.signal}
                    aria-pressed={isSelected}
                    aria-label={signal.customer.name}
                    onClick={() => { if (!isSelected) onSelect(signal); }}
                    key={`${signal.signal_id}:${signal.definition_version}`}
                  >
                    <span className={styles.radio} aria-hidden="true">{isSelected ? <span /> : null}</span>
                    <span className={styles.signalCopy}>
                      <strong>{signal.customer.name}</strong>
                      <span>{signal.customer.description}</span>
                    </span>
                  </button>
                );
              })}
              {percentageSignals.length > 0 ? (
                <div className={styles.signalGroup}>
                  <button
                    type="button"
                    className={styles.disclosure}
                    data-selected={percentageSelected || undefined}
                    aria-expanded={showPercentageReferences}
                    aria-controls={`${choicesId}-percentage`}
                    aria-describedby={percentageSelected ? `${choicesId}-percentage-selection` : undefined}
                    aria-label="Percentage move"
                    onClick={() => {
                      const nextExpanded = !showPercentageReferences;
                      setPercentageExpanded(nextExpanded);
                      onPercentageExpandedChange?.(nextExpanded);
                    }}
                  >
                    <span className={styles.signalCopy}>
                      <strong>Percentage move</strong>
                      <span>Alert when a ticker moves up or down by the percentage you choose.</span>
                    </span>
                    {percentageSelected ? <span id={`${choicesId}-percentage-selection`} className={styles.selectionBadge}>Selected</span> : null}
                    <span className={styles.chevron} aria-hidden="true">{showPercentageReferences ? "−" : "+"}</span>
                  </button>
                  {showPercentageReferences ? (
                    <fieldset
                      id={`${choicesId}-percentage`}
                      ref={percentageReferenceRef}
                      className={styles.referenceChoices}
                      aria-invalid={invalidReference || undefined}
                      aria-describedby={invalidReference ? errorMessageId : undefined}
                    >
                      <legend>Compare with</legend>
                      {percentageSignals.map((signal) => {
                        const presenter = getReviewedSignalPresenter(signal);
                        if (!presenter || presenter.kind !== "PERCENTAGE_MOVE") return null;
                        const isSelected = selected?.signalId === signal.signal_id
                          && selected.definitionVersion === signal.definition_version;
                        return (
                          <label className={styles.referenceChoice} key={presenter.identity}>
                            <input
                              type="radio"
                              name="percentage-reference"
                              checked={isSelected}
                              onChange={() => { if (!isSelected) onSelect(signal); }}
                            />
                            <span>
                              <strong>{presenter.referenceLabel}</strong>
                              <small>{presenter.referenceKind === "PRIOR_REGULAR_CLOSE"
                                ? "Use the last completed regular trading session."
                                : "Use today’s regular-session opening price."}</small>
                            </span>
                          </label>
                        );
                      })}
                    </fieldset>
                  ) : null}
                </div>
              ) : null}
              {movingAverageSignals.length > 0 ? (
                <div className={styles.signalGroup}>
                  <button
                    type="button"
                    className={styles.disclosure}
                    data-selected={movingAverageSelected || undefined}
                    aria-expanded={showMovingAverageComparisons}
                    aria-controls={`${choicesId}-averages`}
                    aria-describedby={movingAverageSelected ? `${choicesId}-averages-selection` : undefined}
                    aria-label="Moving averages"
                    onClick={() => {
                      const nextExpanded = !showMovingAverageComparisons;
                      setMovingAverageExpanded(nextExpanded);
                      onMovingAverageExpandedChange?.(nextExpanded);
                    }}
                  >
                    <span className={styles.signalCopy}>
                      <strong>Moving averages</strong>
                      <span>Track price trends or a change between faster and slower averages.</span>
                    </span>
                    {movingAverageSelected ? <span id={`${choicesId}-averages-selection`} className={styles.selectionBadge}>Selected</span> : null}
                    <span className={styles.chevron} aria-hidden="true">{showMovingAverageComparisons ? "−" : "+"}</span>
                  </button>
                  {showMovingAverageComparisons ? (
                    <fieldset
                      id={`${choicesId}-averages`}
                      ref={movingAverageComparisonRef}
                      className={styles.referenceChoices}
                      aria-invalid={invalidMovingAverageComparison || undefined}
                      aria-describedby={invalidMovingAverageComparison ? errorMessageId : undefined}
                    >
                      <legend>What should we compare?</legend>
                      {hasPriceAverage ? (
                        <label className={styles.referenceChoice}>
                          <input
                            type="radio"
                            name="moving-average-comparison"
                            checked={selectedMovingAverageComparison === "PRICE_TO_AVERAGE"}
                            onChange={() => onSelectMovingAverageComparison?.("PRICE_TO_AVERAGE")}
                          />
                          <span>
                            <strong>Price and a moving average</strong>
                            <small>Watch whether the completed daily close is above, below, or crosses an average.</small>
                          </span>
                        </label>
                      ) : null}
                      {hasAverageCrossover ? (
                        <label className={styles.referenceChoice}>
                          <input
                            type="radio"
                            name="moving-average-comparison"
                            checked={selectedMovingAverageComparison === "AVERAGE_CROSSOVER"}
                            onChange={() => onSelectMovingAverageComparison?.("AVERAGE_CROSSOVER")}
                          />
                          <span>
                            <strong>Two moving averages</strong>
                            <small>Watch a faster average cross above or below a slower average.</small>
                          </span>
                        </label>
                      ) : null}
                    </fieldset>
                  ) : null}
                </div>
              ) : null}
              {momentumSignals.length > 0 ? (
                <div className={styles.signalGroup}>
                  <button
                    type="button"
                    className={styles.disclosure}
                    data-selected={Boolean(selectedMomentumIndicator) || undefined}
                    aria-expanded={showMomentumIndicators}
                    aria-controls={`${choicesId}-momentum`}
                    aria-describedby={selectedMomentumIndicator ? `${choicesId}-momentum-selection` : undefined}
                    aria-label="Momentum"
                    onClick={() => {
                      const nextExpanded = !showMomentumIndicators;
                      setMomentumExpanded(nextExpanded);
                      onMomentumExpandedChange?.(nextExpanded);
                    }}
                  >
                    <span className={styles.signalCopy}>
                      <strong>Momentum</strong>
                      <span>Track a completed-day RSI, MACD, or rate-of-change measurement.</span>
                    </span>
                    {selectedMomentumIndicator ? <span id={`${choicesId}-momentum-selection`} className={styles.selectionBadge}>Selected</span> : null}
                    <span className={styles.chevron} aria-hidden="true">{showMomentumIndicators ? "−" : "+"}</span>
                  </button>
                  {showMomentumIndicators ? (
                    <fieldset
                      id={`${choicesId}-momentum`}
                      ref={momentumIndicatorRef}
                      className={styles.referenceChoices}
                      aria-invalid={invalidMomentumIndicator || undefined}
                      aria-describedby={invalidMomentumIndicator ? errorMessageId : undefined}
                    >
                      <legend>Choose a measurement</legend>
                      {hasMomentumIndicator("RSI") ? (
                        <label className={styles.referenceChoice}>
                          <input
                            type="radio"
                            name="momentum-indicator"
                            checked={selectedMomentumIndicator === "RSI"}
                            onChange={() => onSelectMomentumIndicator?.("RSI")}
                          />
                          <span>
                            <strong>14-day RSI</strong>
                            <small>Compare relative strength with a level you choose.</small>
                          </span>
                        </label>
                      ) : null}
                      {hasMomentumIndicator("MACD") ? (
                        <label className={styles.referenceChoice}>
                          <input
                            type="radio"
                            name="momentum-indicator"
                            checked={selectedMomentumIndicator === "MACD"}
                            onChange={() => onSelectMomentumIndicator?.("MACD")}
                          />
                          <span>
                            <strong>MACD</strong>
                            <small>Watch the MACD line cross its signal line or zero.</small>
                          </span>
                        </label>
                      ) : null}
                      {hasMomentumIndicator("ROC") ? (
                        <label className={styles.referenceChoice}>
                          <input
                            type="radio"
                            name="momentum-indicator"
                            checked={selectedMomentumIndicator === "ROC"}
                            onChange={() => onSelectMomentumIndicator?.("ROC")}
                          />
                          <span>
                            <strong>14-day rate of change</strong>
                            <small>Compare the completed-day price change with a percentage you choose.</small>
                          </span>
                        </label>
                      ) : null}
                    </fieldset>
                  ) : null}
                </div>
              ) : null}
              {volatilitySignals.length > 0 ? (
                <WatchCatalogSignalDisclosure
                  title="Completed-day volatility"
                  description="Track trading range, Bollinger bands, or realized movement after the market day completes."
                  legend="Choose a measurement"
                  signals={volatilitySignals}
                  selected={selected}
                  onSelect={onSelect}
                  onExpandedChange={onVolatilityExpandedChange}
                  invalid={invalidVolatilityMeasurement}
                  errorMessageId={errorMessageId}
                  choicesRef={volatilityMeasurementRef}
                  radioName="completed-daily-volatility-measurement"
                />
              ) : null}
              <WatchAdvancedSignalFamilies
                signals={advancedSignals}
                selected={selected}
                onSelect={onSelect}
              />
            </div>
          ) : (
            <p className={styles.empty} role="status">{view === "POPULAR"
              ? "No common starting points are available. Choose a category to see the signals available for this account."
              : "No signals are available in this category right now."}</p>
          )}
        </>
      ) : (
        <p className={styles.empty} role="status">No market signals are available for this account right now.</p>
      )}
    </section>
  );
}
